import { makeAutoObservable, runInAction } from 'mobx'
import { Store } from './index.stores'
import { IPayment, IPaymentFormData } from '@typings'

export class PaymentStore {
  store: Store
  payments: IPayment[] = []
  loading = false
  error: string | null = null

  constructor(store: Store) {
    this.store = store
    makeAutoObservable(this)
  }

  /**
   * Get all payments for a document
   */
  async getPayments(documentId: number) {
    this.loading = true
    this.error = null
    try {
      const payments = await this.store.api.payment.getPayments(documentId)
      runInAction(() => {
        this.payments = payments
        this.loading = false
      })
      return payments
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to load payments'
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Record a new payment
   */
  async addPayment(documentId: number, data: IPaymentFormData) {
    this.loading = true
    this.error = null
    try {
      const payment = await this.store.api.payment.createPayment(documentId, data)
      runInAction(() => {
        this.payments = [payment, ...this.payments]
        this.loading = false
      })
      return payment
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to record payment'
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Update a payment
   */
  async updatePayment(documentId: number, paymentId: number, data: Partial<IPaymentFormData>) {
    this.loading = true
    this.error = null
    try {
      const payment = await this.store.api.payment.updatePayment(documentId, paymentId, data)
      runInAction(() => {
        const index = this.payments.findIndex(p => p.id === paymentId)
        if (index !== -1) {
          this.payments[index] = payment
        }
        this.loading = false
      })
      return payment
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to update payment'
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Delete a payment
   */
  async deletePayment(documentId: number, paymentId: number) {
    this.loading = true
    this.error = null
    try {
      await this.store.api.payment.deletePayment(documentId, paymentId)
      runInAction(() => {
        this.payments = this.payments.filter(p => p.id !== paymentId)
        this.loading = false
      })
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to delete payment'
        this.loading = false
      })
      throw error
    }
  }

  /**
   * Get total paid amount
   */
  get totalPaid(): number {
    return this.payments.reduce((sum, payment) => sum + payment.amount, 0)
  }

  /**
   * Clear payments (when switching documents)
   */
  clear() {
    this.payments = []
    this.error = null
  }
}
